import { forwardRef, memo, useImperativeHandle } from 'react';

import { ClickAwayListener, Popper as MuiPopper } from '@material-ui/core';
import clsx from 'clsx';

import { useStyles } from './Popper.styles';
import { PopperHandle, PopperProps } from './Popper.types';
import { usePopper } from './hooks/usePopper';
import { useToggle } from './hooks/useToggle';

export const Popper = memo(
  forwardRef<PopperHandle, PopperProps>(
    ({ toggle, open, disabled, disableClickAway, popperProps, toggleProps, children, onOpenChange, onClose }, ref) => {
      const styles = useStyles({ disablePortal: popperProps?.disablePortal });

      const { isOpen, toggleRef, handleOpenPopper, handleClosePopper, handleToggleClick } = useToggle({
        open,
        disabled,
        toggleProps,
        onOpenChange,
      });

      const {
        popperInstanceRef,
        popperContentRef,
        isPositionFixed,
        handlePopperClick,
        handlePopperTouchEnd,
        handleClickAway,
        handleFocusOut,
      } = usePopper({
        toggleRef,
        isOpen,
        disabled,
        disableClickAway,
        popperProps,
        onClose,
        onClosePopper: handleClosePopper,
      });

      useImperativeHandle(ref, () => ({
        open: handleOpenPopper,
        close: handleClosePopper,
        isOpen: () => isOpen,
      }));

      const modifiers = popperProps?.modifiers as Record<string, unknown> | undefined;
      const flipModifier = modifiers?.flip as Record<string, unknown> | undefined;
      const preventOverflowModifier = modifiers?.preventOverflow as Record<string, unknown> | undefined;

      return (
        <>
          <ClickAwayListener onClickAway={handleClickAway}>
            <span
              aria-describedby="mouse-over-popper"
              {...toggleProps}
              ref={toggleRef}
              onClick={handleToggleClick}
              onBlur={e => {
                handleFocusOut(e);
                toggleProps?.onBlur?.(e);
              }}
            >
              {toggle}
            </span>
          </ClickAwayListener>

          <MuiPopper
            transition={true}
            placement="bottom"
            {...popperProps}
            id={['mouse-over-popper', popperProps?.id].filter(Boolean).join(' ')}
            open={isOpen}
            popperRef={popperInstanceRef as any}
            // Only read while open, after the toggle span has mounted.
            anchorEl={() => toggleRef.current as HTMLElement}
            modifiers={{
              ...popperProps?.modifiers,
              flip: {
                enabled: true,
                ...(flipModifier ?? {}),
              },
              preventOverflow: {
                enabled: true,
                boundariesElement: 'viewport',
                priority: ['left', 'right'],
                ...(preventOverflowModifier ?? {}),
              },
            }}
            popperOptions={{
              positionFixed: isPositionFixed,
            }}
            className={clsx(styles.popper, popperProps?.className)}
            onClick={handlePopperClick}
            onTouchEnd={handlePopperTouchEnd}
          >
            <div ref={popperContentRef} tabIndex={-1} data-popper-content="true" onBlur={handleFocusOut}>
              {children}
            </div>
          </MuiPopper>
        </>
      );
    },
  ),
);
